import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Send, RotateCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import client from '../../api/client';
import { addressStatus, ADDRESS_STATUS } from './addressStatus';

const reach = (g) => [g.phone && 'Text', g.email && 'Email'].filter(Boolean).join(' + ') || 'No contact';

export const AddressRequestDialog = ({ open, onOpenChange, guests, onSent, toast }) => {
  const candidates = useMemo(() => guests.filter((g) => ['missing', 'needs_fix', 'requested'].includes(addressStatus(g))), [guests]);
  const [sel, setSel] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const nonResponders = candidates.filter((g) => addressStatus(g) === 'requested').length;

  useEffect(() => {
    if (open) setSel(Object.fromEntries(candidates.filter((g) => addressStatus(g) !== 'requested' && (g.phone || g.email)).map((g) => [g.id, true])));
    setPreview(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPreview = async () => {
    setBusy(true);
    try { setPreview((await client.post('/address-requests/preview', { guestIds: ids })).data); }
    catch (e) { toast?.({ title: 'Could not preview', description: e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const finish = (data) => {
    const parts = [data.sent && `${data.sent} sent`, data.failed && `${data.failed} could not be delivered (check guest texting authorization, phone and email)`, data.queued && `${data.queued} queued for your send window`, data.skipped && `${data.skipped} skipped (no phone or email)`].filter(Boolean);
    toast?.({ title: data.failed && !data.sent && !data.queued ? 'Address requests not delivered' : 'Address requests on their way', variant: data.failed && !data.sent && !data.queued ? 'destructive' : undefined, description: parts.join(' · ') || 'Nothing to send.' });
    onSent?.(); onOpenChange(false);
  };
  const run = async (fn) => {
    setBusy(true);
    try { finish((await fn()).data); } catch (e) { toast?.({ title: 'Could not send', description: e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="address-request-dialog" className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Request mailing addresses</DialogTitle>
          <DialogDescription>Each guest gets a personal link to a one-page form. We text if they have a phone, email if they have an email, or both.</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <>
            <div className="overflow-auto flex-1 min-h-0 divide-y divide-[#F2E8DE]">
              {candidates.map((g) => {
                const [label, cls] = ADDRESS_STATUS[addressStatus(g)];
                const disabled = !g.phone && !g.email;
                return (
                  <label key={g.id} className={`flex items-center gap-3 py-2 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
                    <input data-testid={`address-req-select-${g.id}`} type="checkbox" disabled={disabled} className="accent-[#7B2C3B]" checked={!!sel[g.id]} onChange={(e) => setSel({ ...sel, [g.id]: e.target.checked })} />
                    <span className="flex-1 text-sm text-[#2A2A2A]">{g.name}</span>
                    <span className="text-[11px] text-[#9A8A7E]">{reach(g)}</span>
                    <span className={`text-[10px] border px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                  </label>
                );
              })}
              {candidates.length === 0 && <p className="text-sm text-[#9A8A7E] py-6 text-center">Every guest has an address on file.</p>}
            </div>
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#F2E8DE] flex-wrap">
              <button data-testid="address-req-resend" disabled={!nonResponders || busy} onClick={() => run(() => client.post('/address-requests/resend'))} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] inline-flex items-center gap-1.5 disabled:opacity-40">
                <RotateCw className="w-3.5 h-3.5" /> Resend to non-responders ({nonResponders})
              </button>
              <button data-testid="address-req-preview" disabled={!ids.length || busy} onClick={loadPreview} className="text-xs px-4 py-2 rounded-full bg-[#7B2C3B] text-white disabled:opacity-40 inline-flex items-center gap-1.5">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Preview message ({ids.length})
              </button>
            </div>
          </>
        ) : (
          <div data-testid="address-req-preview-panel" className="space-y-3">
            <div className="rounded-2xl bg-[#E0F2FE]/50 border border-[#BAE6FD] p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#0369A1] mb-1">Text message</p>
              <p data-testid="address-req-preview-sms" className="text-sm text-[#2A2A2A] whitespace-pre-wrap">{preview.sms}</p>
            </div>
            {(preview.counts.email + preview.counts.both) > 0 && <div className="rounded-2xl bg-[#FFF8E1]/60 border border-[#FCE3A6] p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#B45309] mb-1">Email · {preview.emailSubject}</p>
              <p className="text-sm text-[#2A2A2A]">{preview.emailText}</p>
            </div>}
            <p data-testid="address-req-preview-counts" className="text-xs text-[#6A6A6A]">
              {preview.total} guest{preview.total === 1 ? '' : 's'}: {preview.counts.sms} by text, {preview.counts.email} by email, {preview.counts.both} by both.
              {!preview.inSendWindow && ' It is outside your send window, so these will go out automatically when it opens.'}
              {' '}Texts follow STOP/HELP rules and need your one-time guest-texting authorization.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#F2E8DE]">
              <button onClick={() => setPreview(null)} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8]">Back</button>
              <button data-testid="address-req-send" disabled={busy} onClick={() => run(() => client.post('/address-requests/send', { guestIds: ids }))} className="text-xs px-4 py-2 rounded-full bg-[#7B2C3B] text-white disabled:opacity-40 inline-flex items-center gap-1.5">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send requests
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddressRequestDialog;
